import { Fragment } from "react";
import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";
import styled from "styled-components";

import {
    INVALID_DATA_TYPE_WARNING,
    INVALID_DATA_TYPE_WARNING_BODY,
    INVALID_DATA_TYPE_WARNING_COMPATIBLE,
} from "../cells/constants";
import { formatCrmTypeLabel } from "../utils";
import { VALUE_TYPE_BADGE_COLORS, VALUE_TYPE_I18N_KEY } from "../valueType";
import type { SimilarwebValueType } from "../valueType";

type InvalidDataTypeTooltipContentProps = {
    valueType?: SimilarwebValueType;
    compatibleCrmTypes?: string[];
    crmName?: string;
};

const HighlightedValueType = styled.span<{ $color: string }>`
    ${({ $color }) => setFontVariant("body2", $color)};
    font-weight: 700;
`;

const CompatibleCrmTypes = ({ types, crmName }: { types: string[]; crmName: string }) => {
    const { translate } = useTranslate();

    return (
        <Text
            fontVariant="body2"
            color={colorsPalettes.carbon[0]}
            data-automation="export-table-invalid-data-type-compatible"
        >
            {`${translate(INVALID_DATA_TYPE_WARNING_COMPATIBLE, { crm: crmName })} `}
            {types.map((type, index) => (
                <Fragment key={type}>
                    {index > 0 ? ", " : null}
                    <HighlightedValueType
                        $color={colorsPalettes.yellow[400]}
                        data-automation={`export-table-invalid-data-type-compatible-${type}`}
                    >
                        {formatCrmTypeLabel(type)}
                    </HighlightedValueType>
                </Fragment>
            ))}
        </Text>
    );
};

export const InvalidDataTypeTooltipContent = ({
    valueType,
    compatibleCrmTypes = [],
    crmName = "CRM",
}: InvalidDataTypeTooltipContentProps) => {
    const { translate } = useTranslate();
    const hasCompatibleCrmTypes = compatibleCrmTypes.length > 0;

    if (hasCompatibleCrmTypes) {
        return (
            <Stack
                gap="4px"
                data-automation="export-table-invalid-data-type-tooltip"
            >
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[0]}
                >
                    {translate(INVALID_DATA_TYPE_WARNING_BODY)}
                </Text>
                <CompatibleCrmTypes
                    types={compatibleCrmTypes}
                    crmName={crmName}
                />
            </Stack>
        );
    }

    if (!valueType) {
        return (
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[0]}
                data-automation="export-table-invalid-data-type-tooltip"
            >
                {translate(INVALID_DATA_TYPE_WARNING_BODY)}
            </Text>
        );
    }

    const [before, after] = translate(INVALID_DATA_TYPE_WARNING).split("%type%");
    const { color } = VALUE_TYPE_BADGE_COLORS[valueType];

    return (
        <Text
            fontVariant="body2"
            color={colorsPalettes.carbon[0]}
            data-automation="export-table-invalid-data-type-tooltip"
        >
            {before}
            <HighlightedValueType
                $color={color}
                data-automation="export-table-invalid-data-type-value"
            >
                {translate(VALUE_TYPE_I18N_KEY[valueType])}
            </HighlightedValueType>
            {after}
        </Text>
    );
};
